import { useMutation } from '@tanstack/react-query'

import { useEventCapture } from '@surglogs-app/analytics'
import { useHandler } from '@surglogs-app/hooks'
import { Config } from '@surglogs-app/types'
import { useToast } from '@surglogs-app/ui.toast'

import { PromoModalContext } from '../constants/Analytics'
import { PromoAuthData } from '../models'

interface RequestData {
  center_id: number
  center_name: string | undefined
  user_name: string
  user_email: string
  module_name: string
}

interface TrialRequestSubmitHandlerProps {
  featureName: string
  onSuccess: (data: FreeTrialFormValues) => void
  isLoginAd?: boolean
  config: Omit<Config, 'onlyofficeUrl'>
  context?: PromoModalContext
  authData?: PromoAuthData
  canManageFacility?: boolean
}

export interface FreeTrialFormValues {
  email: string
}

export const useTrialRequestSubmitHandler = ({
  featureName,
  onSuccess,
  isLoginAd = false,
  config,
  context,
  authData,
  canManageFacility,
}: TrialRequestSubmitHandlerProps) => {
  const { showErrorToast } = useToast()
  const { captureEvent } = useEventCapture()
  const { mutateAsync, isPending } = useMutation<unknown, unknown, RequestData>(
    {
      mutationFn: (data) =>
        fetch(config.trialRequestUrl, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },
  )
  const canCreateRequest = authData?.clinicReport === true || isLoginAd

  const onSubmit = useHandler(async ({ email }: FreeTrialFormValues) => {
    const requestData = {
      center_id: Number(authData?.clinicId),
      center_name: authData?.institutionName,
      user_name: authData?.userName ?? '',
      user_email: email,
      module_name: featureName,
      host_name: window.location.hostname,
      origin: 'web',
    }

    try {
      if (canCreateRequest) {
        await mutateAsync(requestData)
        onSuccess({ email })
        captureEvent('feature_requested', {
          type: featureName,
          context,
          can_manage_facility: canManageFacility,
        })
      }
    } catch (error) {
      showErrorToast({ error })
    }
  })

  return {
    onSubmit,
    isPending,
    canCreateRequest,
  }
}
